/**
 * Home-equity (heloc / heloan) leads and subscriptions store their amounts
 * differently from mortgage records: `loan_amount` is the borrower's EXISTING
 * mortgage balance and the requested line / loan lives in `line_amount`.
 * Widget builds before 2026-09 sent the requested line as `cash_out`, so that
 * is the fallback until every stored record has been backfilled.
 */
export const HOME_EQUITY_LOAN_TYPES = ["heloc", "heloan"] as const

export const isHomeEquityLoanType = (loanType?: string | null): boolean =>
  !!loanType &&
  (HOME_EQUITY_LOAN_TYPES as readonly string[]).includes(loanType.toLowerCase())

const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

interface HomeEquityAmountsSource {
  loan_type?: string | null
  loan_amount?: number | string | null
  line_amount?: number | string | null
  cash_out?: number | string | null
}

/**
 * Resolves the two home-equity amounts, or null for any other loan type.
 * `lineAmountSource` says which stored field supplied the line so a caller
 * can tell a migrated record from a legacy one.
 */
export const getHomeEquityAmounts = (
  details?: HomeEquityAmountsSource | null
): {
  lineAmount: number
  mortgageBalance: number
  lineAmountSource: "line_amount" | "cash_out" | "none"
} | null => {
  if (!details || !isHomeEquityLoanType(details.loan_type)) return null
  const line = toNumber(details.line_amount)
  const legacy = toNumber(details.cash_out)
  const lineAmountSource =
    line > 0 ? "line_amount" : legacy > 0 ? "cash_out" : "none"
  return {
    lineAmount: line > 0 ? line : legacy,
    mortgageBalance: toNumber(details.loan_amount),
    lineAmountSource
  }
}
